import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper, Tabs, Tab } from '@mui/material';
import PageHeader from '../components/PageHeader';
import ConfigurationWarning from '../components/common/ConfigurationWarning';

// Import Shopify to ERP the tab components
import ShopifyToErpSettingsTab from '../components/customer-sync/shopify-to-erp/SettingsTab';
import ShopifyToErpSyncedCustomersTab from '../components/customer-sync/shopify-to-erp/SyncedCustomersTab';
import ShopifyToErpComponentSelector from '../components/customer-sync/shopify-to-erp/ComponentSelector';

// Import ERP to Shopify the tab components
import ErpToShopifyComponentSelector from '../components/customer-sync/erp-to-shopify/ComponentSelector';
import ErpToShopifySyncOperationsTab from '../components/customer-sync/erp-to-shopify/SyncOperationsTab';
import ErpToShopifySettingsTab from '../components/customer-sync/erp-to-shopify/SettingsTab';
import ErpToShopifySyncedCustomersTab from '../components/customer-sync/erp-to-shopify/SyncedCustomersTab';
import ErpToShopifyFieldMappingTab from '../components/customer-sync/erp-to-shopify/FieldMappingTab';

export default function CustomerSync() {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active main tab and sub tab based on the current URL
  const getActiveTabsFromPath = () => {
    const path = location.pathname;

    // Determine main tab (direction)
    let mainTab = 0; // Default to Shopify to ERP
    if (path.includes('/erp-to-shopify')) {
      mainTab = 1; // ERP to Shopify
    }

    // Determine sub tab based on direction
    let subTab = 0; // Default
    if (mainTab === 0) {
      // Shopify to ERP: only customers (0) and settings (1)
      if (path.includes('/customers')) subTab = 0;
      if (path.includes('/settings')) subTab = 1;
    } else {
      // ERP to Shopify: operations (0), customers (1), mapping (2), settings (3)
      if (path.includes('/customers')) subTab = 1;
      if (path.includes('/mapping')) subTab = 2;
      if (path.includes('/settings')) subTab = 3;
    }

    return { mainTab, subTab };
  };

  const [activeMainTab, setActiveMainTab] = useState(
    getActiveTabsFromPath().mainTab
  );
  const [activeSubTab, setActiveSubTab] = useState(
    getActiveTabsFromPath().subTab
  );

  // Update active tabs when the location changes
  useEffect(() => {
    const { mainTab, subTab } = getActiveTabsFromPath();
    setActiveMainTab(mainTab);
    setActiveSubTab(subTab);

    // If we're at the base customer-sync URL, redirect to default
    if (
      location.pathname === '/customer-sync' ||
      location.pathname === '/customer-sync/'
    ) {
      navigate('/customer-sync/shopify-to-erp/customers', { replace: true });
    }
  }, [location, navigate]);

  // Handle main tab change
  const handleMainTabChange = (event, newValue) => {
    setActiveMainTab(newValue);

    // Navigate to the correct direction with appropriate default sub-tab
    const direction = newValue === 0 ? 'shopify-to-erp' : 'erp-to-shopify';
    const subPath = newValue === 0 ? 'customers' : 'operations'; // Different defaults for each direction

    navigate(`/customer-sync/${direction}/${subPath}`);
  };

  // Handle sub tab change (this will be passed to ComponentSelector)
  const handleSubTabChange = (newValue) => {
    setActiveSubTab(newValue);
  };

  return (
    <Box>

      <ConfigurationWarning justShowContentWhenNoConfig={true}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <PageHeader
            title='Customer Synchronization'
            subtitle='Sync customers from your ERP system to Shopify'
          />
        </Box>
      </ConfigurationWarning>
      <ConfigurationWarning>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' }
          }}
        >
          <PageHeader
            title='Customer Synchronization'
            subtitle='Sync customers from your ERP system to Shopify'
          />

          {/* Main Tabs - Direction */}
          <Paper
            elevation={0}
            sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', boxShadow: 10, width: { xs: '100%', sm: 'auto' } }}
          >
            <Tabs
              value={activeMainTab}
              onChange={handleMainTabChange}
              variant='fullWidth'
              indicatorColor='primary'
              textColor='primary'
              sx={{
                '& .MuiTab-root': {
                  minHeight: 64,
                  fontSize: '1rem',
                  fontWeight: 600
                }
              }}
            >
              <Tab label='Shopify to ERP' />
              <Tab label='ERP to Shopify' />
            </Tabs>
          </Paper>
        </Box>

        {activeMainTab === 0 ? (
          <ShopifyToErpComponentSelector
            activeTab={activeSubTab}
            onTabChange={handleSubTabChange}
            direction={activeMainTab === 0 ? 'shopify-to-erp' : 'erp-to-shopify'}
          />
        ) : (
          <ErpToShopifyComponentSelector
            activeTab={activeSubTab}
            onTabChange={handleSubTabChange}
            direction={activeMainTab === 0 ? 'shopify-to-erp' : 'erp-to-shopify'}
          />
        )}

        {/* Sub Tabs - Component Selector */}

        {/* Render the active tab content based on direction */}
        {activeMainTab === 0 ? (
          // Shopify to ERP content (2 tabs: customers=0, settings=1)
          <>
            {activeSubTab === 0 && (
              <ShopifyToErpSyncedCustomersTab tabChange={activeSubTab} />
            )}
            {activeSubTab === 1 && <ShopifyToErpSettingsTab />}
          </>
        ) : (
          // ERP to Shopify content (4 tabs: operations=0, customers=1, mapping=2, settings=3)
          <>
            {activeSubTab === 0 && <ErpToShopifySyncOperationsTab />}
            {activeSubTab === 1 && <ErpToShopifySyncedCustomersTab />}
            {activeSubTab === 2 && <ErpToShopifyFieldMappingTab />}
            {activeSubTab === 3 && <ErpToShopifySettingsTab />}
          </>
        )}
      </ConfigurationWarning>
    </Box>
  );
}
